/** Oscillator layer controls — the main control surface for the synth.
 *
 * Each layer has its own waveform, pitch offset, volume, ADSR envelope,
 * and filter. Link toggles propagate ADSR/filter changes across all layers.
 */

import {
	AudioLines,
	Eye,
	EyeOff,
	Layers,
	Link,
	Plus,
	Square,
	Trash2,
	Triangle,
	Unlink,
	Waves,
} from "lucide-react";
import type React from "react";
import { useCallback, useRef, useState } from "react";
import { DEFAULT_WAVETABLES, type WavetableId } from "../lib/wavetables.ts";
import type {
	ADSRParams,
	FilterParams,
	OscillatorLayer,
	WaveformType,
} from "../types/synth.ts";
import { MAX_LAYERS } from "../types/synth.ts";

// ---- Inline ADSR helpers (same exponential mapping as ADSRControls) ----

type ADSRSliderDef = {
	readonly key: keyof ADSRParams;
	readonly label: string;
	readonly min: number;
	readonly max: number;
	readonly unit: string;
	readonly exponential: boolean;
};

const ADSR_SLIDERS: readonly ADSRSliderDef[] = [
	{
		key: "attack",
		label: "A",
		min: 0.001,
		max: 5,
		unit: "s",
		exponential: true,
	},
	{
		key: "decay",
		label: "D",
		min: 0.001,
		max: 5,
		unit: "s",
		exponential: true,
	},
	{ key: "sustain", label: "S", min: 0, max: 1, unit: "", exponential: false },
	{
		key: "release",
		label: "R",
		min: 0.001,
		max: 10,
		unit: "s",
		exponential: true,
	},
] as const;

function timeToSlider(value: number, min: number, max: number): number {
	const logMin = Math.log(min);
	const logMax = Math.log(max);
	return (
		(Math.log(Math.max(min, Math.min(max, value))) - logMin) / (logMax - logMin)
	);
}

function sliderToTime(position: number, min: number, max: number): number {
	const logMin = Math.log(min);
	const logMax = Math.log(max);
	return Math.exp(logMin + position * (logMax - logMin));
}

function formatTime(val: number, unit: string): string {
	if (unit === "s") {
		return val >= 1 ? `${val.toFixed(1)}s` : `${(val * 1000).toFixed(0)}ms`;
	}
	return `${(val * 100).toFixed(0)}%`;
}

// ---- Inline filter helpers (same log mapping as FilterControls) ----

function linearToLog(value: number): number {
	const minLog = Math.log(20);
	const maxLog = Math.log(20000);
	return Math.exp(minLog + value * (maxLog - minLog));
}

function logToLinear(freq: number): number {
	const minLog = Math.log(20);
	const maxLog = Math.log(20000);
	return (Math.log(freq) - minLog) / (maxLog - minLog);
}

function formatFreq(hz: number): string {
	return hz >= 1000 ? `${(hz / 1000).toFixed(1)}kHz` : `${hz.toFixed(0)}Hz`;
}

// ---- Types ----

/** Props for the OscillatorLayers component. */
export type OscillatorLayersProps = {
	readonly layers: readonly OscillatorLayer[];
	readonly adsrLinked: boolean;
	readonly filterLinked: boolean;
	readonly onLayersChange: (layers: readonly OscillatorLayer[]) => void;
	readonly onLayerUpdate: (
		layerId: string,
		update: Partial<OscillatorLayer>,
	) => void;
	readonly onLayerADSR: (layerId: string, adsr: Partial<ADSRParams>) => void;
	readonly onLayerFilter: (
		layerId: string,
		filter: Partial<FilterParams>,
	) => void;
	readonly onAdsrLinkedChange: (linked: boolean) => void;
	readonly onFilterLinkedChange: (linked: boolean) => void;
};

type WaveformOption = {
	readonly type: WaveformType;
	readonly label: string;
	readonly Icon: React.ComponentType<{ readonly size: number }>;
};

/** Single smooth sine wave — one full cycle, drawn as a cubic bezier. */
function SineWaveIcon({ size }: { readonly size: number }) {
	const mid = size / 2;
	const amp = size * 0.34;
	const w = size;
	// Single sine period using two cubic bezier curves
	const d = `M 0 ${mid} C ${w * 0.18} ${mid - amp}, ${w * 0.32} ${mid - amp}, ${w * 0.5} ${mid} S ${w * 0.82} ${mid + amp}, ${w} ${mid}`;
	return (
		<svg
			width={size}
			height={size}
			viewBox={`0 0 ${size} ${size}`}
			fill="none"
			stroke="currentColor"
			strokeWidth={1.8}
			strokeLinecap="round"
			aria-hidden="true"
		>
			<path d={d} />
		</svg>
	);
}

const WAVEFORM_OPTIONS: readonly WaveformOption[] = [
	{ type: "sine", label: "Sin", Icon: SineWaveIcon },
	{ type: "sawtooth", label: "Saw", Icon: AudioLines },
	{ type: "square", label: "Sqr", Icon: Square },
	{ type: "triangle", label: "Tri", Icon: Triangle },
	{ type: "wavetable", label: "Wvt", Icon: Waves },
] as const;

let layerCounter = 1;

function generateLayerId(): string {
	layerCounter += 1;
	return `layer-${layerCounter}-${Date.now()}`;
}

/** Small waveform preview drawn from raw PCM samples. */
function MiniWaveform({
	samples,
	width = 80,
	height = 20,
}: {
	readonly samples: readonly number[];
	readonly width?: number;
	readonly height?: number;
}) {
	const numPoints = Math.min(samples.length, 80);
	const step = samples.length / numPoints;
	const mid = height / 2;
	const points = Array.from({ length: numPoints }, (_, i) => {
		const x = (i / Math.max(numPoints - 1, 1)) * width;
		const y = mid - (samples[Math.floor(i * step)] ?? 0) * mid * 0.85;
		return `${x.toFixed(1)},${y.toFixed(1)}`;
	}).join(" ");
	return (
		<svg
			width={width}
			height={height}
			viewBox={`0 0 ${width} ${height}`}
			fill="none"
			stroke="currentColor"
			strokeWidth={1.2}
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
		>
			<polyline points={points} />
		</svg>
	);
}

/** Wavetable selection panel — appears when the wavetable waveform is active. */
type WavetablePanelProps = {
	readonly currentWavetableId: string | undefined;
	readonly hasCustomWavetable: boolean;
	/** PCM samples for custom wavetable — used to render the mini-waveform preview. */
	readonly customWavetableSamples: readonly number[] | undefined;
	readonly onSelectWavetable: (id: WavetableId) => void;
	readonly onCustomWavetable: (samples: readonly number[]) => void;
};

function WavetablePanel({
	currentWavetableId,
	hasCustomWavetable,
	customWavetableSamples,
	onSelectWavetable,
	onCustomWavetable,
}: WavetablePanelProps) {
	const [isDragOver, setIsDragOver] = useState(false);
	const [dropError, setDropError] = useState<string | null>(null);
	const [isDecoding, setIsDecoding] = useState(false);
	const fileInputRef = useRef<HTMLInputElement>(null);

	const handleDragOver = useCallback(
		(e: React.DragEvent<HTMLButtonElement>) => {
			e.preventDefault();
			setIsDragOver(true);
		},
		[],
	);

	const handleDragLeave = useCallback(() => {
		setIsDragOver(false);
	}, []);

	const processFile = useCallback(
		async (file: File) => {
			setDropError(null);
			setIsDecoding(true);
			try {
				const arrayBuffer = await file.arrayBuffer();
				const audioCtx = new OfflineAudioContext(1, 1, 44100);
				const decoded = await audioCtx.decodeAudioData(arrayBuffer);
				const channelData = decoded.getChannelData(0);
				// Take up to 4096 samples from the start
				const samples = Array.from(channelData.slice(0, 4096));
				onCustomWavetable(samples);
				onSelectWavetable("custom");
			} catch {
				setDropError("Could not decode audio file. Try a WAV or OGG file.");
			} finally {
				setIsDecoding(false);
			}
		},
		[onCustomWavetable, onSelectWavetable],
	);

	const handleDrop = useCallback(
		async (e: React.DragEvent<HTMLButtonElement>) => {
			e.preventDefault();
			setIsDragOver(false);
			const file = e.dataTransfer.files[0];
			if (!file) return;
			await processFile(file);
		},
		[processFile],
	);

	const handleFileChange = useCallback(
		async (e: React.ChangeEvent<HTMLInputElement>) => {
			const file = e.target.files?.[0];
			if (!file) return;
			await processFile(file);
			// Reset input so the same file can be re-selected
			if (fileInputRef.current) fileInputRef.current.value = "";
		},
		[processFile],
	);

	const activeId = currentWavetableId ?? "mellow-saw";

	return (
		<div className="wavetable-panel">
			<div className="wavetable-presets">
				{DEFAULT_WAVETABLES.map((wt) => (
					<button
						key={wt.id}
						type="button"
						className={`wavetable-preset-btn ${activeId === wt.id ? "active" : ""}`}
						onClick={() => onSelectWavetable(wt.id)}
						title={wt.label}
					>
						{wt.label}
					</button>
				))}
				{hasCustomWavetable && (
					<button
						type="button"
						className={`wavetable-preset-btn ${activeId === "custom" ? "active" : ""}`}
						onClick={() => onSelectWavetable("custom")}
						title="Custom wavetable"
					>
						Custom
					</button>
				)}
			</div>

			{/* Drag-and-drop / click-to-upload zone */}
			<button
				type="button"
				className={`wavetable-drop-zone ${isDragOver ? "drag-over" : ""} ${isDecoding ? "decoding" : ""} ${hasCustomWavetable ? "loaded" : ""}`}
				onDragOver={handleDragOver}
				onDragLeave={handleDragLeave}
				onDrop={handleDrop}
				onClick={() => fileInputRef.current?.click()}
				title={
					hasCustomWavetable
						? "Custom wavetable loaded — drop a new file to replace"
						: "Drop an audio file here or click to browse"
				}
				aria-label="Drop audio file for custom wavetable"
			>
				{isDecoding ? (
					"Decoding…"
				) : isDragOver ? (
					"Drop to load"
				) : hasCustomWavetable && customWavetableSamples ? (
					<>
						<MiniWaveform samples={customWavetableSamples} />
						<span className="wavetable-loaded-label">Custom ✓</span>
					</>
				) : (
					"Drop audio file"
				)}
			</button>
			{dropError && <span className="wavetable-drop-error">{dropError}</span>}
			<input
				ref={fileInputRef}
				type="file"
				accept="audio/*"
				className="wavetable-file-input"
				onChange={handleFileChange}
				aria-label="Upload audio file for wavetable"
			/>
		</div>
	);
}

export function OscillatorLayers({
	layers,
	adsrLinked,
	filterLinked,
	onLayersChange,
	onLayerUpdate,
	onLayerADSR,
	onLayerFilter,
	onAdsrLinkedChange,
	onFilterLinkedChange,
}: OscillatorLayersProps) {
	const [activeTab, setActiveTab] = useState(0);

	const handleAddLayer = useCallback(() => {
		if (layers.length >= MAX_LAYERS) return;
		// Copy ADSR/filter from the first layer as starting point
		const source = layers[0]!;
		const newLayer: OscillatorLayer = {
			id: generateLayerId(),
			waveform: "sawtooth",
			semitoneOffset: 0,
			centOffset: 0,
			gain: 0.7,
			pan: 0,
			adsr: { ...source.adsr },
			filter: { ...source.filter },
			enabled: true,
		};
		const next = [...layers, newLayer];
		onLayersChange(next);
		setActiveTab(next.length - 1);
	}, [layers, onLayersChange]);

	const handleRemoveLayer = useCallback(
		(index: number) => {
			if (layers.length <= 1) return;
			const next = layers.filter((_, i) => i !== index);
			onLayersChange(next);
			if (activeTab >= next.length) {
				setActiveTab(Math.max(0, next.length - 1));
			}
		},
		[layers, onLayersChange, activeTab],
	);

	const currentLayer = layers[activeTab];

	return (
		<div className="control-group osc-layers-group">
			{/* Header with label and link toggles */}
			<div className="osc-header">
				<span className="control-label">
					<Layers size={14} />
					Layers
				</span>
				<div className="osc-link-toggles">
					<button
						type="button"
						className={`osc-link-btn ${adsrLinked ? "linked" : ""}`}
						onClick={() => onAdsrLinkedChange(!adsrLinked)}
						title={
							adsrLinked
								? "ADSR linked — affects all layers"
								: "ADSR independent per layer"
						}
					>
						{adsrLinked ? <Link size={10} /> : <Unlink size={10} />}
						<span>Env</span>
					</button>
					<button
						type="button"
						className={`osc-link-btn ${filterLinked ? "linked" : ""}`}
						onClick={() => onFilterLinkedChange(!filterLinked)}
						title={
							filterLinked
								? "Filter linked — affects all layers"
								: "Filter independent per layer"
						}
					>
						{filterLinked ? <Link size={10} /> : <Unlink size={10} />}
						<span>Flt</span>
					</button>
				</div>
			</div>

			{/* Tab bar */}
			<div className="osc-tabs">
				{layers.map((layer, i) => (
					<div key={layer.id} className="osc-tab-wrapper">
						<button
							type="button"
							className={`osc-tab ${i === activeTab ? "active" : ""} ${!layer.enabled ? "disabled-layer" : ""}`}
							onClick={() => setActiveTab(i)}
							title={`Layer ${i + 1}: ${layer.waveform}${!layer.enabled ? " (disabled)" : ""}`}
						>
							<span>L{i + 1}</span>
						</button>
						<button
							type="button"
							className={`osc-tab-toggle ${layer.enabled ? "enabled" : ""}`}
							onClick={() =>
								onLayerUpdate(layer.id, { enabled: !layer.enabled })
							}
							title={layer.enabled ? "Disable layer" : "Enable layer"}
						>
							{layer.enabled ? <Eye size={8} /> : <EyeOff size={8} />}
						</button>
					</div>
				))}
				{layers.length < MAX_LAYERS && (
					<button
						type="button"
						className="osc-tab osc-tab-add"
						onClick={handleAddLayer}
						title="Add oscillator layer"
					>
						<Plus size={12} />
					</button>
				)}
			</div>

			{/* Per-layer controls */}
			{currentLayer && (
				<div className="osc-layer-controls">
					{/* ---- Waveform ---- */}
					<div className="osc-section-label">Waveform</div>
					<div className="osc-waveform-area">
						<div className="osc-waveform-row">
							{WAVEFORM_OPTIONS.map(({ type, label, Icon }) => (
								<button
									key={type}
									type="button"
									className={`osc-waveform-btn ${currentLayer.waveform === type ? "active" : ""}`}
									onClick={() =>
										onLayerUpdate(currentLayer.id, { waveform: type })
									}
									title={label}
								>
									<Icon size={14} />
								</button>
							))}
						</div>
						{currentLayer.waveform === "wavetable" && (
							<WavetablePanel
								currentWavetableId={currentLayer.wavetableId}
								hasCustomWavetable={
									currentLayer.wavetableId === "custom" &&
									(currentLayer.customWavetable?.length ?? 0) > 0
								}
								customWavetableSamples={currentLayer.customWavetable}
								onSelectWavetable={(id) =>
									onLayerUpdate(currentLayer.id, { wavetableId: id })
								}
								onCustomWavetable={(samples) =>
									onLayerUpdate(currentLayer.id, {
										wavetableId: "custom",
										customWavetable: samples,
									})
								}
							/>
						)}
					</div>

					{/* ---- Pitch ---- */}
					<div className="osc-section-label">Pitch</div>
					<div className="osc-param-row">
						<span className="osc-param-label">Semi</span>
						<input
							type="range"
							min={-24}
							max={24}
							step={1}
							value={currentLayer.semitoneOffset}
							onChange={(e) =>
								onLayerUpdate(currentLayer.id, {
									semitoneOffset: Number(e.target.value),
								})
							}
							className="osc-param-slider"
							aria-label="Semitone offset"
						/>
						<span className="osc-param-value">
							{currentLayer.semitoneOffset > 0
								? `+${currentLayer.semitoneOffset}`
								: currentLayer.semitoneOffset}
						</span>
					</div>
					<div className="osc-param-row">
						<span className="osc-param-label">Cent</span>
						<input
							type="range"
							min={-100}
							max={100}
							step={1}
							value={currentLayer.centOffset}
							onChange={(e) =>
								onLayerUpdate(currentLayer.id, {
									centOffset: Number(e.target.value),
								})
							}
							className="osc-param-slider"
							aria-label="Cent offset"
						/>
						<span className="osc-param-value">
							{currentLayer.centOffset > 0
								? `+${currentLayer.centOffset}`
								: currentLayer.centOffset}
							¢
						</span>
					</div>

					{/* ---- Volume ---- */}
					<div className="osc-param-row">
						<span className="osc-param-label">Vol</span>
						<input
							type="range"
							min={0}
							max={1}
							step={0.01}
							value={currentLayer.gain}
							onChange={(e) =>
								onLayerUpdate(currentLayer.id, {
									gain: Number(e.target.value),
								})
							}
							className="osc-param-slider"
							aria-label="Layer volume"
						/>
						<span className="osc-param-value">
							{Math.round(currentLayer.gain * 100)}%
						</span>
					</div>

					{/* ---- Pan ---- */}
					<div className="osc-param-row">
						<span className="osc-param-label">Pan</span>
						<input
							type="range"
							min={-1}
							max={1}
							step={0.01}
							value={currentLayer.pan}
							onChange={(e) =>
								onLayerUpdate(currentLayer.id, {
									pan: Number(e.target.value),
								})
							}
							className="osc-param-slider"
							aria-label="Layer pan"
						/>
						<span className="osc-param-value">
							{currentLayer.pan === 0
								? "C"
								: currentLayer.pan < 0
									? `L${Math.round(Math.abs(currentLayer.pan) * 100)}`
									: `R${Math.round(currentLayer.pan * 100)}`}
						</span>
					</div>

					{/* ---- Envelope (ADSR) ---- */}
					<div className="osc-section-label">
						Envelope
						{adsrLinked && <span className="osc-link-badge">linked</span>}
					</div>
					<div className="osc-adsr-row">
						{ADSR_SLIDERS.map(({ key, label, min, max, unit, exponential }) => {
							const val = currentLayer.adsr[key];
							const sliderVal = exponential ? timeToSlider(val, min, max) : val;
							const sliderMin = exponential ? 0 : min;
							const sliderMax = exponential ? 1 : max;
							const sliderStep = exponential ? 0.001 : 0.01;

							return (
								<div key={key} className="osc-adsr-slider">
									<span className="osc-adsr-label">{label}</span>
									<input
										type="range"
										className="osc-adsr-input"
										// @ts-expect-error -- non-standard 'orient' attr for Firefox vertical range
										orient="vertical"
										min={sliderMin}
										max={sliderMax}
										step={sliderStep}
										value={sliderVal}
										onChange={(e) => {
											const raw = Number(e.target.value);
											const realVal = exponential
												? sliderToTime(raw, min, max)
												: raw;
											onLayerADSR(currentLayer.id, { [key]: realVal });
										}}
										aria-label={`${label} ${key}`}
									/>
									<span className="osc-adsr-value">
										{formatTime(val, unit)}
									</span>
								</div>
							);
						})}
					</div>

					{/* ---- Filter ---- */}
					<div className="osc-section-label">
						Filter
						{filterLinked && <span className="osc-link-badge">linked</span>}
					</div>
					<div className="osc-param-row">
						<span className="osc-param-label">Cut</span>
						<input
							type="range"
							min={0}
							max={1}
							step={0.001}
							value={logToLinear(currentLayer.filter.cutoff)}
							onChange={(e) =>
								onLayerFilter(currentLayer.id, {
									cutoff: linearToLog(Number(e.target.value)),
								})
							}
							className="osc-param-slider"
							aria-label="Filter cutoff"
						/>
						<span className="osc-param-value">
							{formatFreq(currentLayer.filter.cutoff)}
						</span>
					</div>
					<div className="osc-param-row">
						<span className="osc-param-label">Res</span>
						<input
							type="range"
							min={0.1}
							max={30}
							step={0.1}
							value={currentLayer.filter.resonance}
							onChange={(e) =>
								onLayerFilter(currentLayer.id, {
									resonance: Number(e.target.value),
								})
							}
							className="osc-param-slider"
							aria-label="Filter resonance"
						/>
						<span className="osc-param-value">
							{currentLayer.filter.resonance.toFixed(1)}
						</span>
					</div>

					{/* Remove button (only if > 1 layer) */}
					{layers.length > 1 && (
						<button
							type="button"
							className="osc-remove-btn"
							onClick={() => handleRemoveLayer(activeTab)}
							title="Remove this layer"
						>
							<Trash2 size={12} />
							<span>Remove Layer</span>
						</button>
					)}
				</div>
			)}
		</div>
	);
}
